import { Link } from "react-router-dom";
import { Mail, MapPin, ShieldCheck, ArrowRight } from "lucide-react";

const COLS = [
  {
    title: "Investor Relations",
    links: [
      { to: "/investors", label: "Investor Portal" },
      { to: "/investors#phases", label: "Development Phases" },
      { to: "/contact?topic=investor", label: "Request Packet" },
      { to: "/portal/investor", label: "Investor Dashboard" },
    ],
  },
  {
    title: "Veteran Partnerships",
    links: [
      { to: "/veteran-housing", label: "Housing Initiative" },
      { to: "/veteran-housing", label: "Apply for Housing" },
      { to: "/contact?topic=partner", label: "VA Partnerships" },
      { to: "/careers", label: "Veteran Careers" },
    ],
  },
  {
    title: "Nonprofit Collaborations",
    links: [
      { to: "/foundation", label: "220 Community Foundation" },
      { to: "/foundation#tabs", label: "Donate to Foundation" },
      { to: "/foundation#tabs", label: "Volunteer" },
      { to: "/foundation#tabs", label: "Become a Partner" },
    ],
  },
  {
    title: "Careers & Workforce",
    links: [
      { to: "/careers", label: "Open Roles" },
      { to: "/education", label: "Trade Programs" },
      { to: "/education", label: "Apprenticeships" },
      { to: "/careers", label: "Veteran Priority" },
    ],
  },
  {
    title: "Development Opportunities",
    links: [
      { to: "/realty", label: "Property Listings" },
      { to: "/subsidiaries/land-development", label: "Land Development" },
      { to: "/subsidiaries/construction", label: "Contractor Bidding" },
      { to: "/contact?topic=consult", label: "Build with Us" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-[#08080A] border-t border-white/10 mt-24" data-testid="site-footer">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Join the Mission</div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Build with us. Invest with us. Serve with us.
            </h3>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/investors" className="btn-primary text-sm">Invest in Phase 1 <ArrowRight size={14} /></Link>
            <Link to="/contact?topic=partner" className="btn-ghost text-sm">Partner With Us</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="text-3xl font-display tracking-tight">
              <span className="text-white">220</span>
              <span className="text-gold">.</span>
              <span className="text-silver">Inc</span>
            </div>
            <p className="mt-6 text-white/60 leading-relaxed text-sm">
              A veteran-owned umbrella corporation building the future of community development,
              affordable housing, and economic opportunity.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/60 border border-white/10 px-3 py-2">
              <ShieldCheck size={12} className="text-gold" /> Veteran-Owned · Mission-Driven
            </div>
            <ul className="mt-8 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3"><MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" /> Columbus, GA · Phenix City, AL</li>
              <li className="flex items-start gap-3"><Mail size={14} className="text-gold mt-0.5 flex-shrink-0" /> Travis.McGruder@220inc.com</li>
            </ul>
          </div>

          <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">{c.title}</div>
                <ul className="space-y-3 text-sm">
                  {c.links.map((l) => (
                    <li key={l.label}><Link to={l.to} className="text-white/75 hover:text-gold transition-colors">{l.label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#06060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 text-[11px] text-white/45 leading-relaxed" data-testid="forward-looking-statement">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-3">Forward-Looking Statements</div>
          <p>
            220 Inc. is a veteran-owned platform currently in active planning and development. References to subsidiaries,
            divisions, properties, the 220 Event Center, the 220 Community Foundation, the Veteran Housing Initiative,
            services, programs, units, jobs, capital, returns, partnerships, and timelines on this site are forward-looking
            and reflect master-planning intent — not realized operations, ownership of assets, completed acquisitions,
            or services currently being delivered. Figures shown are targets, projections, or modeled estimates and are subject to change.
            No solicitation of investment is intended by the content of this website; any offering will be made only to
            qualified investors via formal disclosure documents.
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} 220 Inc. — Building the Future. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Investor Disclosures</a>
            <a href="#" className="hover:text-white">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
